import { Plus } from 'lucide-react'
import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { breadcrumbsFor, isSettingsRoute, navIconFor, type Crumb } from '@/lib/navigation'

import { Button } from '@/components/ui/button'

interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  onAddClick?: () => void
  addButtonText?: string
  /** The add button shows only when the staff member holds this permission. */
  addPermission?: string
  /** data-copilot-id for the add button, so the assistant can point at it. */
  addButtonCopilotId?: string
  badge?: ReactNode
  /** Extra actions on the right, before the add button. */
  actions?: ReactNode
  /** Rendered before the title (e.g. a back link). */
  back?: ReactNode
  /** Override the trail derived from the URL, or `false` to hide the parent link. */
  breadcrumbs?: Crumb[] | false
  /**
   * `compact` (lists): title with the description underneath. `large` (forms and detail pages): the back button,
   * icon and "Parent /" link on the top row with the actions, then the title as a 26px heading.
   */
  size?: 'compact' | 'large'
  className?: string
}

/** The parent page to show as "Parent / Title" when the page sits below its nav entry (e.g. /products/new). */
function parentOf(trail: Crumb[]): Crumb | undefined {
  const last = trail.at(-1)
  const parent = trail.at(-2)
  return last && !last.to && parent?.to ? parent : undefined
}

/**
 * Page header. `compact` (lists): an optional "Parent /" link and a 20px title, the description muted underneath, and
 * the page's actions on the right. `size="large"` is the form/detail variant.
 */
export const PageHeader = ({
  title,
  description,
  onAddClick: onAddClickProp,
  addButtonText = 'Add Item',
  addPermission,
  addButtonCopilotId,
  badge,
  actions,
  back,
  breadcrumbs,
  size = 'compact',
  className,
}: PageHeaderProps) => {
  const { pathname } = useLocation()
  const { can } = useAuth()
  const onAddClick = addPermission && !can(addPermission) ? undefined : onAddClickProp
  const trail =
    breadcrumbs === false
      ? []
      : (breadcrumbs ?? breadcrumbsFor(pathname, typeof title === 'string' ? title : undefined))
  // In settings mode the settings nav already shows where you are, so the section icon and parent link are dropped.
  const inSettings = isSettingsRoute(pathname)
  const parent = inSettings ? undefined : parentOf(trail)
  const Icon = inSettings ? undefined : navIconFor(pathname)
  const actionRow = (actions || onAddClick) && (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {actions}
      {onAddClick && (
        <Button onClick={onAddClick} data-copilot-id={addButtonCopilotId}>
          <Plus />
          {addButtonText}
        </Button>
      )}
    </div>
  )

  if (size === 'large') {
    // With no back link, icon or parent (settings pages), the actions share the title row instead of sitting alone above it.
    const hasLead = !!(back || Icon || parent)
    return (
      <div className={cn('mb-5 space-y-3', className)}>
        {hasLead && (
          <div className="flex min-h-8 flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              {back}
              {Icon && <Icon className="size-4 shrink-0 text-ink" aria-hidden="true" />}
              {parent && (
                <nav aria-label="Breadcrumb" className="text-sm font-medium">
                  <Link to={parent.to!} className="text-text-2 hover:text-ink hover:underline">
                    {parent.label}
                  </Link>
                </nav>
              )}
            </div>
            {actionRow}
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="min-w-0 truncate text-heading-xl text-ink">{title}</h1>
              {badge}
            </div>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          {!hasLead && actionRow}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      <div className="min-w-0 space-y-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {back}
          {parent && (
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm font-medium">
              <Link to={parent.to!} className="text-text-2 hover:text-ink hover:underline">
                {parent.label}
              </Link>
              <span className="text-faint" aria-hidden="true">
                /
              </span>
            </nav>
          )}
          <h1 className="truncate text-heading-lg text-ink">{title}</h1>
          {badge}
        </div>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actionRow}
    </div>
  )
}
